import { Battery, Wifi, Activity } from "lucide-react"
import { formatDistanceToNow } from "date-fns"

const DeviceStatus = ({ devices }) => {
  const getBatteryIcon = (battery) => {
    if (battery > 75) return <Battery className="h-5 w-5 text-green-500" />
    if (battery > 25) return <Battery className="h-5 w-5 text-yellow-500" />
    return <Battery className="h-5 w-5 text-red-500" />
  }

  const getHealthColor = (health) => {
    switch (health) {
      case "excellent":
        return "text-green-600"
      case "good":
        return "text-green-500"
      case "fair":
        return "text-yellow-500"
      case "poor":
        return "text-red-500"
      default:
        return "text-gray-500"
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Device Status</h3>
      </div>

      <div className="divide-y divide-gray-200">
        {devices.map((device) => (
          <div key={device.id} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-medium text-gray-900">{device.name}</h4>
                <p className="text-sm text-gray-600">{device.id}</p>
              </div>
              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-md device-status-${device.status}`}>
                {device.status.toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                {getBatteryIcon(device.battery)}
                <div>
                  <p className="text-sm font-medium text-gray-900">Battery</p>
                  <p className="text-sm text-gray-600">{device.battery}%</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Wifi className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Last Signal</p>
                  <p className="text-sm text-gray-600">
                    {formatDistanceToNow(new Date(device.lastSignal), { addSuffix: true })}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Activity className={`h-5 w-5 ${getHealthColor(device.health)}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Health</p>
                  <p className={`text-sm ${getHealthColor(device.health)}`}>
                    {device.health.charAt(0).toUpperCase() + device.health.slice(1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Chemical Sensor</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-md ${
                      device.sensors.chemical.status === "normal"
                        ? "bg-green-100 text-green-800"
                        : device.sensors.chemical.status === "elevated"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {device.sensors.chemical.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {device.sensors.chemical.value} / {device.sensors.chemical.threshold} ppm
                </p>
              </div>

              <div className="bg-gray-50 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Vibration Sensor</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-md ${
                      device.sensors.vibration.status === "normal"
                        ? "bg-green-100 text-green-800"
                        : device.sensors.vibration.status === "elevated"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {device.sensors.vibration.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {device.sensors.vibration.value} Hz / Threshold: {device.sensors.vibration.threshold} Hz
                </p>
                {device.sensors.vibration.lastDetectedType && (
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-700">Last Detection:</span>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        device.sensors.vibration.lastDetectedType === "bomb"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {device.sensors.vibration.lastDetectedType.toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>Foot: {device.sensors.vibration.footVibrationThreshold} Hz</div>
                  <div>Bomb: {device.sensors.vibration.bombVibrationThreshold} Hz</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default DeviceStatus

